import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import agent from "../../api/agent";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { License } from "../models/Licenses/License";
import DownloadIcon from "@mui/icons-material/Download";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";

interface Props {
  userIdForChecking?: number;
  closeForm?: () => void;
}

export default function LicenseByUser({ userIdForChecking, closeForm }: Props) {
  const [licenseList, setLicenseList] = useState<License[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Authentication
  const [token, setToken] = useState<string>("");
  const [username, setUsername] = useState<string>("");

  // boolean for checking this is called by userid or user profile
  const [isUserProfile, setIsUserProfile] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get token from localStorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          const decodedToken: any = jwtDecode(tokenFLocal);
          let userId: number;

          // Check the condition, if the userIdForChecking is empty, means is call by normal user
          if (
            userIdForChecking === undefined ||
            userIdForChecking === null ||
            userIdForChecking === 0
          ) {
            // decode the jwt and get the user id
            userId = decodedToken.sub;
            setIsUserProfile(true);
          } else {
            userId = userIdForChecking;
            setIsUserProfile(false);
          }
          // console.log(userId);

          const username = decodedToken.name;
          const trimmedUsername = username.replace(/\s+/g, "");
          setUsername(trimmedUsername);

          const licenseResponse = await agent.License.getLicenseByUser(
            userId,
            tokenFLocal,
          );
          setLicenseList(licenseResponse);
          setToken(tokenFLocal);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLicenseDownloadOnClick = async (
    licenseKey: string,
    prefixName: string | undefined,
  ) => {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (!licenseKey) return;
    setLoading(true);
    try {
      // Get the date format in "MMDDYY" format
      const formattedDate = new Date()
        .toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "");
      const response = await agent.License.downloadLicense(licenseKey, token);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${prefixName}_license_${username}_${formattedDate}.lic`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      alert("License download failed. Please check the License Key.");
    } finally {
      setLoading(false);
    }
  };

  // Function for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;

  const PaginationComponent = () => (
  <TablePagination
    rowsPerPageOptions={[10, 25, 50, 100]}
    component="div"
    count={licenseList.length}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={handleChangePage}
    onRowsPerPageChange={handleChangeRowsPerPage}
    sx={{
      ".MuiTablePagination-toolbar": {
        paddingLeft: 2,
      },
    }}
  />
);

  return (
    <Box sx={{ p: 2 }}>
      {isUserProfile ? (
        <Typography variant="h5" sx={{ mb: 3 }}>
          License
        </Typography>
      ) : null}

      <TableContainer component={Paper}>
        {/* <PaginationComponent />  */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Software Name</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Valid Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenseList
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((license) => (
                <TableRow key={license.licenseId}>
                  <TableCell>{license.softwareName}</TableCell>
                  <TableCell>{license.startDate}</TableCell>
                  <TableCell>{license.validDate}</TableCell>
                  <TableCell>
                    {license.status === "Used" ? (
                      <Chip label="Used" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Unused" color="error" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {/* Button to redownload the lic file */}
                      <Tooltip title="Download License File">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleLicenseDownloadOnClick(
                              license.licenseKey,
                              license.prefixName,
                            )
                          }
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {/* Pagination Component */}
        <PaginationComponent />
      </TableContainer>

      {!isUserProfile && 
      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Button variant="outlined" onClick={closeForm}>
          Cancel
        </Button>
      </Box>}
    </Box>
  );
}
